import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/components/ui/utils';
import useCategories from '@/hooks/use-categories';
import appServices from '@/services/appServices';
import {
  AppCategory,
  DEFAULT_CATEGORY_COLORS,
  UNCATEGORIZED_FILTER,
  getTextColorForBackground,
} from '@polybank/shared';
import { Check, ChevronsUpDown, Plus, Search, Tag, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

type SingleSelectProps = {
  multiSelect?: false;
  currentCategoryId?: string;
  onCategoryChange: (category?: AppCategory) => void;
};

type MultiSelectProps = {
  multiSelect: true;
  selectedCategoryIds: string[];
  onSelectionChange: (categoryIds: string[]) => void;
};

type CategoryPickerProps = (SingleSelectProps | MultiSelectProps) & {
  disabled?: boolean;
  placeholder?: string;
};

export function CategoryPicker(props: CategoryPickerProps) {
  const { disabled, placeholder } = props;
  const isMulti = props.multiSelect === true;

  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_CATEGORY_COLORS[0].bg);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { data, isLoading, addItem, categoriesMap } = useCategories();
  const categories = useMemo(() => data.rows ?? [], [data.rows]);

  // Single-select internal state
  const singleCurrentCategoryId = !isMulti
    ? (props as SingleSelectProps).currentCategoryId
    : undefined;
  const singleOnCategoryChange = !isMulti
    ? (props as SingleSelectProps).onCategoryChange
    : undefined;

  // Multi-select props
  const multiSelectedIds = useMemo(
    () => (isMulti ? (props as MultiSelectProps).selectedCategoryIds : []),
    [isMulti, props]
  );
  const multiOnSelectionChange = isMulti
    ? (props as MultiSelectProps).onSelectionChange
    : undefined;

  const [selectedCategoryId, setselectedCategoryId] = useState(
    singleCurrentCategoryId
  );

  // Sync internal state with prop changes (single-select only)
  useEffect(() => {
    if (!isMulti) {
      setselectedCategoryId(singleCurrentCategoryId);
    }
  }, [isMulti, singleCurrentCategoryId]);

  // Categories list for multi-select includes "Uncategorized" virtual entry
  const multiCategories = useMemo(() => {
    if (!isMulti) return [];
    const uncategorized = {
      $id: UNCATEGORIZED_FILTER,
      name: t('transactionsFilters.uncategorized'),
    } as AppCategory;
    return [uncategorized, ...categories];
  }, [isMulti, categories, t]);

  // Filter categories based on search
  const filteredCategories = (isMulti ? multiCategories : categories).filter(
    (category) =>
      category &&
      category.name &&
      category.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Check if search value matches any existing category exactly
  const exactMatch = categories.find(
    (category) =>
      category &&
      category.name &&
      category.name.toLowerCase() === searchValue.toLowerCase()
  );

  // Find current category object (single-select)
  const currentCategoryObj = selectedCategoryId
    ? categoriesMap[selectedCategoryId]
    : undefined;

  // Single-select handler
  const selectCategory = useCallback(
    (category?: AppCategory) => {
      setselectedCategoryId(category?.$id);
      singleOnCategoryChange?.(category);
      setOpen(false);
      setSearchValue('');
    },
    [singleOnCategoryChange]
  );

  // Multi-select toggle handler
  const toggleCategory = useCallback(
    (categoryId: string) => {
      if (!multiOnSelectionChange) return;
      if (multiSelectedIds.includes(categoryId)) {
        multiOnSelectionChange(
          multiSelectedIds.filter((id) => id !== categoryId)
        );
      } else {
        multiOnSelectionChange([...multiSelectedIds, categoryId]);
      }
    },
    [multiOnSelectionChange, multiSelectedIds]
  );

  // Multi-select remove handler (for badges)
  const removeCategory = useCallback(
    (categoryId: string) => {
      if (!multiOnSelectionChange) return;
      multiOnSelectionChange(
        multiSelectedIds.filter((id) => id !== categoryId)
      );
    },
    [multiOnSelectionChange, multiSelectedIds]
  );

  // Get display label for a category ID (multi-select badges)
  const getCategoryLabel = useCallback(
    (categoryId: string) => {
      if (categoryId === UNCATEGORIZED_FILTER) {
        return t('transactionsFilters.uncategorized');
      }
      return categoriesMap[categoryId]?.name ?? categoryId;
    },
    [categoriesMap, t]
  );

  const onCreateCategory = useCallback(async () => {
    const newCategory = await appServices.categories.createCategory({
      name: searchValue.trim(),
      color: newColor,
    });
    addItem(newCategory);
    setIsCreateModalOpen(false);
    selectCategory(newCategory);
  }, [newColor, searchValue, selectCategory, addItem]);

  if (isLoading) {
    return (
      <Button
        variant='outline'
        className={cn(
          'justify-between h-8 px-2',
          isMulti ? 'w-full min-h-10' : 'min-w-[180px]'
        )}
        disabled
      >
        <div className='flex items-center gap-2'>
          <Tag className='h-3 w-3' />
          <span>{t('common.loading')}...</span>
        </div>
        <ChevronsUpDown className='ml-2 h-3 w-3 shrink-0 opacity-50' />
      </Button>
    );
  }

  // Trigger button content
  const renderTrigger = () => {
    if (isMulti) {
      const count = multiSelectedIds.length;
      return (
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between min-h-10'
          disabled={disabled}
        >
          <span className='truncate'>
            {count > 0
              ? `${count} ${t('common.selected')}`
              : (placeholder ?? t('categoryPicker.selectCategory'))}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      );
    }

    return (
      <Button
        variant='outline'
        role='combobox'
        aria-expanded={open}
        className={cn(
          'justify-between min-w-[180px] h-8 px-2',
          !singleCurrentCategoryId && 'text-muted-foreground'
        )}
        disabled={disabled}
      >
        {currentCategoryObj ? (
          <div className='flex items-center gap-2'>
            <span className='text-xs'>{currentCategoryObj.icon}</span>
            <span className='truncate'>{currentCategoryObj.name}</span>
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <Tag className='h-3 w-3' />
            <span>{placeholder ?? t('categoryPicker.selectCategory')}</span>
          </div>
        )}
        <ChevronsUpDown className='ml-2 h-3 w-3 shrink-0 opacity-50' />
      </Button>
    );
  };

  return (
    <>
      <div className={cn(isMulti && 'space-y-2')}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
          <PopoverContent
            className='w-[280px] p-0 max-h-[300px] flex flex-col'
            align='start'
            usePortal={false}
          >
            {/* Search Input */}
            <div className='flex items-center border-b px-3 shrink-0'>
              <Search className='mr-2 h-4 w-4 shrink-0 opacity-50' />
              <Input
                placeholder={t('common.searchCategories')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className='h-10 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0'
              />
            </div>

            {/* Category List */}
            <div className='max-h-[250px] overflow-y-auto overscroll-contain'>
              <div className='p-1'>
                {/* Create new category option (single-select only) */}
                {!isMulti && searchValue.trim() && !exactMatch && (
                  <div
                    onClick={() => setIsCreateModalOpen(true)}
                    className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none text-blue-600 hover:bg-accent'
                  >
                    <Plus className='mr-2 h-4 w-4' />
                    {t('categoryPicker.createNew', {
                      name: searchValue.trim(),
                    })}
                  </div>
                )}

                {filteredCategories.length === 0 && !searchValue.trim() ? (
                  <div className='py-6 text-center text-sm text-muted-foreground'>
                    {t('common.noCategoriesFound')}
                  </div>
                ) : isMulti ? (
                  /* Multi-select: all categories with toggle */
                  filteredCategories.map((category) => {
                    const catId = category.$id!;
                    const isSelected = multiSelectedIds.includes(catId);
                    const isUncategorized = catId === UNCATEGORIZED_FILTER;
                    return (
                      <div
                        key={catId}
                        className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground'
                        onClick={() => toggleCategory(catId)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <div className='flex items-center gap-2 flex-1'>
                          {!isUncategorized && (
                            <span className='text-sm'>{category.icon}</span>
                          )}
                          <span
                            className={cn(
                              isUncategorized && 'text-muted-foreground'
                            )}
                          >
                            {category.name}
                          </span>
                        </div>
                        {!isUncategorized && category.color && (
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: category.color }}
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  /* Single-select: uncategorized + categories */
                  <>
                    {/* Uncategorized option */}
                    <div
                      className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground'
                      onClick={() => selectCategory(undefined)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          !singleCurrentCategoryId ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className='text-muted-foreground'>
                        {t('categoryPicker.uncategorized')}
                      </span>
                    </div>

                    {/* Existing categories */}
                    {filteredCategories.map((category) => (
                      <div
                        key={category.$id}
                        className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground'
                        onClick={() => selectCategory(category)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedCategoryId === category.$id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        <div className='flex items-center gap-2 flex-1'>
                          <span className='text-sm'>{category.icon}</span>
                          <span>{category.name}</span>
                        </div>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: category.color }}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Multi-select: Selected Items Badges */}
        {isMulti && multiSelectedIds.length > 0 && (
          <div className='flex flex-wrap gap-1'>
            {multiSelectedIds.map((categoryId) => (
              <Badge
                key={categoryId}
                variant='secondary'
                className='gap-1 text-xs'
              >
                {getCategoryLabel(categoryId)}
                <button
                  type='button'
                  onClick={() => removeCategory(categoryId)}
                  className='hover:bg-destructive/20 rounded-full p-0.5 ml-1'
                >
                  <X className='w-3 h-3' />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Create Category Dialog (single-select only) */}
      {!isMulti && (
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle className='flex items-center gap-2'>
                <Plus className='h-5 w-5 text-blue-600 dark:text-blue-400' />
                {t('categoryPicker.createNewCategory')}
              </DialogTitle>
            </DialogHeader>

            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label>{t('categoryPicker.categoryName')}</Label>
                <Input
                  id='category-name'
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t('categoryPicker.enterCategoryName')}
                />
              </div>

              <div className='space-y-3'>
                <Label>{t('categoryPicker.color')}</Label>

                {/* Color Picker */}
                <HexColorPicker
                  color={newColor}
                  onChange={setNewColor}
                  style={{ width: '100%' }}
                />

                {/* Hex Input */}
                <div className='flex items-center gap-2'>
                  <span className='text-muted-foreground font-mono'>#</span>
                  <Input
                    value={newColor.replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9A-Fa-f]/g, '');
                      if (value.length <= 6) {
                        setNewColor('#' + value);
                      }
                    }}
                    maxLength={6}
                    className='font-mono uppercase'
                    placeholder='2ECC71'
                  />
                </div>

                {/* Quick Preset Swatches */}
                <div className='flex flex-wrap gap-1.5'>
                  {DEFAULT_CATEGORY_COLORS.map((color) => (
                    <button
                      key={color.bg}
                      type='button'
                      className={cn(
                        'w-6 h-6 rounded-full border-2 transition-transform',
                        newColor === color.bg
                          ? 'scale-110 border-primary ring-2 ring-primary/30'
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color.bg }}
                      onClick={() => setNewColor(color.bg)}
                    />
                  ))}
                </div>

                {/* Live Preview */}
                {searchValue.trim() && (
                  <div className='pt-3 border-t'>
                    <Label className='text-xs text-muted-foreground mb-2 block'>
                      {t('categoryPicker.preview')}
                    </Label>
                    <div
                      className='inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium'
                      style={{
                        backgroundColor: newColor,
                        color: getTextColorForBackground(newColor),
                      }}
                    >
                      <Tag className='h-3 w-3' />
                      {searchValue.trim()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => setIsCreateModalOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={onCreateCategory}>
                {t('categoryPicker.createCategory')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
